// Global Instructions Rule Applied!
//
// Light localStorage persistence for the mobile "recently viewed" list. The shared
// `active_records` slice can't serve as a recents store — it's in-memory only, has no
// recency ordering, and evicts a record when its detail screen unmounts. So mobile keeps
// its own tiny, capped, ordered list here so recents survive a cold PWA relaunch.
//
// All access is SSR-safe (`typeof window`) and wrapped so a parse error or a
// `QuotaExceededError` degrades to "no recents" rather than throwing — recents are
// best-effort, never load-bearing.

export interface RecentItem {
  /** The record's primary id (stringified). */
  id: string
  /** Route module: 'companies' | 'deals' | 'contacts' | 'funds'. */
  module: string
  /** Human-visible name (record title). */
  title: string
  /** Optional secondary line (industry, company, role, etc.). */
  subtitle?: string
  /** Epoch ms of the most recent view (used for ordering). */
  ts: number
}

const STORAGE_KEY = 'reservoir.mobile.recents.v1'

/** Max entries kept (most-recent-first). Small by design — it's a quick-access list. */
export const RECENTS_CAP = 15

function isRecentItem(value: unknown): value is RecentItem {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  return typeof v.id === 'string' && typeof v.module === 'string' && typeof v.title === 'string'
}

/** Read the persisted recents (tolerant: drops malformed entries, caps length). */
export function loadRecents(): RecentItem[] {
  if (typeof window === 'undefined' || !window.localStorage) return []
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isRecentItem).slice(0, RECENTS_CAP)
  } catch {
    return []
  }
}

/** Persist the recents list (swallows quota / serialization errors). */
export function saveRecents(items: RecentItem[]): void {
  if (typeof window === 'undefined' || !window.localStorage) return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, RECENTS_CAP)))
  } catch {
    // ignore — recents are best-effort and must never break the app
  }
}
